import { useTranslations } from "@jugl-web/utils";
import { SubscriptionPlanModuleId } from "@jugl-web/rest-api";
import { useWorkspaceFeatureInfo } from "@web-src/modules/workspace/hooks/useWorkspaceFeatureInfo/useWorkspaceFeatureInfo";
import { useState, useEffect } from "react";
import { Dialog, PlainButton, Button } from "@jugl-web/ui-components";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import useEmblaCarousel from "embla-carousel-react";
import { useWorkspaceFeatureCardData } from "@web-src/modules/workspace/hooks/useWorkspaceFeatureCardData/useWorkspaceFeatureCardData";
import { Lottie } from "@jugl-web/utils/utils/Lottie";
import { InfoStep } from "./components/InfoStep/InfoStep";
import { VideoStep } from "./components/VideoStep/VideoStep";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as CarouselArrowIcon } from "./assets/carousel-arrow-left.svg";
import { ReactComponent as DotIcon } from "./assets/carousel-dot.svg";
import { ReactComponent as ActiveDotIcon } from "./assets/carousel-active-dot.svg";
import confettiAnimation from "./assets/confetti-animation.json";
import { PaidFeatureDialog } from "../PaidFeatureDialog";

export const WorkspaceFeatureDialog = ({
  feature,
  isOpen,
  isShowcaseFlow,
  shouldShowConfetti,
  onClose,
}: {
  feature: SubscriptionPlanModuleId | null;
  isOpen: boolean;
  isShowcaseFlow?: boolean;
  shouldShowConfetti?: boolean;
  onClose: () => void;
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showConfetti, setShowConfetti] = useState(!!shouldShowConfetti);
  const [isPaidFeatureDialogOpen, setIsPaidFeatureDialogOpen] = useState(false);
  const [currentFeature, setCurrentFeature] =
    useState<SubscriptionPlanModuleId | null>(feature);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    containScroll: "trimSnaps",
  });

  useEffect(() => {
    if (isOpen) {
      setShowConfetti(!!shouldShowConfetti);
    }
  }, [shouldShowConfetti, isOpen]);

  const { t } = useTranslations();
  const { navigateToPage } = useNavigation();
  const featureInfo = useWorkspaceFeatureInfo();
  const moduleData = useWorkspaceFeatureCardData();
  const module = currentFeature ? moduleData[currentFeature] : undefined;
  const featureData = currentFeature ? featureInfo[currentFeature] : undefined;
  const [infoStep, videoStep] = currentFeature
    ? featureInfo[currentFeature]?.steps ?? []
    : [];

  const handleClose = () => {
    onClose();
    setCurrentStep(0);
  };

  useEffect(() => {
    if (feature) {
      setCurrentFeature(feature);
    }
  }, [feature]);

  useEffect(() => {
    if (emblaApi) {
      emblaApi.on("select", () => {
        if (emblaApi.selectedScrollSnap() !== currentStep) {
          setCurrentStep(emblaApi.selectedScrollSnap());
        }
      });
    }
  }, [currentStep, emblaApi]);

  return (
    <>
      <Dialog
        isOpen={isOpen && !isPaidFeatureDialogOpen}
        onClose={handleClose}
        className="font-secondary z-[1399] flex max-h-[95vh] w-[95vw] max-w-[1310px] flex-col overflow-hidden"
      >
        {showConfetti && (
          <Lottie
            options={{
              animationData: confettiAnimation,
              loop: false,
              autoplay: true,
              rendererSettings: {
                className: "absolute z-10 top-0 pointer-events-none",
              },
            }}
            isClickToPauseDisabled
          />
        )}
        <div className="border-dark-100 flex h-[60px] w-full items-center justify-between border-0 border-b-[1px] border-solid pl-[26px] pr-6">
          <div className="flex items-center gap-3">
            {featureData?.titleIcon}
            <div className="text-lg font-semibold text-[#383838]">
              {featureData?.title}
            </div>
          </div>
          <PlainButton
            onClick={handleClose}
            className="flex items-center justify-center"
          >
            <CloseIcon />
          </PlainButton>
        </div>
        <div className="embla overflow-y-auto" ref={emblaRef}>
          <div className="embla__container">
            <div className="embla__slide">
              {infoStep && <InfoStep {...infoStep} />}
            </div>
            <div className="embla__slide">
              {videoStep && <VideoStep {...videoStep} />}
            </div>
          </div>
        </div>

        <div className="border-dark-100 z-20 flex h-[77px] items-center justify-between border-0 border-t-[2px] border-solid bg-white px-[40px]">
          <div />
          <div className="item-center flex gap-4 pl-[180px]">
            <CarouselArrowIcon
              onClick={() => {
                emblaApi?.scrollPrev();
              }}
              className="cursor-pointer"
            />
            <div className="flex items-center gap-2">
              {currentStep === 0 ? (
                <ActiveDotIcon />
              ) : (
                <DotIcon
                  onClick={() => {
                    setCurrentStep(0);
                    emblaApi?.scrollTo(0);
                  }}
                />
              )}
              {currentStep === 1 ? (
                <ActiveDotIcon />
              ) : (
                <DotIcon
                  onClick={() => {
                    setCurrentStep(1);
                    emblaApi?.scrollTo(1);
                  }}
                />
              )}
            </div>
            <CarouselArrowIcon
              className="rotate-180 cursor-pointer"
              onClick={() => {
                emblaApi?.scrollNext();
              }}
            />
          </div>
          {isShowcaseFlow || module?.isUpcoming ? (
            <Button
              color="grey"
              className="bg-grey-100 text-dark-700 font-secondary h-[40px] w-[199px] font-normal"
              onClick={() => {
                handleClose();
              }}
            >
              {t({
                id: "common.back",
                defaultMessage: "Back",
              })}
            </Button>
          ) : (
            <div className="flex items-center gap-4">
              {module?.isEnabled || module?.isCoreFeature ? (
                <>
                  <Button
                    className="font-secondary h-[40px] w-[172px]"
                    variant="outlined"
                    onClick={() => {
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      navigateToPage(
                        infoStep?.navigateTo ?? "workspaceFeatures"
                      );
                    }}
                  >
                    {t({
                      id: "workspace-features-page.module-info-layout.explore-feature",
                      defaultMessage: "Explore Feature",
                    })}
                  </Button>
                  <Button
                    className="font-secondary h-[40px] w-[150px]"
                    onClick={onClose}
                  >
                    {t({
                      id: "common.close",
                      defaultMessage: "Close",
                    })}
                  </Button>
                </>
              ) : (
                <Button
                  className="font-secondary h-[40px] w-[150px]"
                  onClick={async () => {
                    if (module?.isLicensed) {
                      try {
                        await module?.onToggle?.();
                        setShowConfetti(true);
                      } catch (error) {
                        // pass
                      }
                    } else {
                      setIsPaidFeatureDialogOpen(true);
                    }
                  }}
                >
                  {t({
                    id: "workspace-features-page.enable-feature",
                    defaultMessage: "Enable Feature",
                  })}
                </Button>
              )}
            </div>
          )}
        </div>
      </Dialog>
      <PaidFeatureDialog
        isOpen={isPaidFeatureDialogOpen}
        feature={module}
        onRequestClose={() => {
          setIsPaidFeatureDialogOpen(false);
        }}
      />
    </>
  );
};
